import axios from 'axios';
import classNames from 'classnames';
import Button from 'components/base/Button';
import * as formik from 'formik';
import { useState } from 'react';
import { Col, Form, Row, Table, Badge } from 'react-bootstrap';
import Card from 'react-bootstrap/Card';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { clinicValidationSchma } from 'static/Clinic';
import * as yup from 'yup';
import QRCode from 'qrcode';

const SAAddClinic = () => {
  const navigate = useNavigate();
  const { Formik } = formik;
  const token = localStorage.getItem('token');

  const schema = yup.object().shape(clinicValidationSchma);
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [createdClinicUser, setCreatedClinicUser] = useState<any>(null);
  const onSubmit = async (values: any) => {
    const data = {
      clinic: {
        name: values.name,
        email: values.email,
        code: values.code,
        phone: values.phone,
        address: values.address,
        postcode: values.postcode
      },
      user: {
        firstname: values.headDietitianName,
        email: values.headDietitianEmail,
        password: values.password,
        clinical_export_enabled: values.clinical_export_enabled,
        research_export_enabled: values.research_export_enabled
      }
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/clinic/sa`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data['status'] == false) {
        toast.error(response.data['error']['message']);
        return false;
      }

      toast(response.data['data']['message']);
      setCreatedClinicUser({
        firstname: response.data['data']['user']?.firstname || values.name,
        email: response.data['data']['user']?.email || values.email,
        password: values.password,
        role: 'clinic'
      });
    } catch (error) {
      console.error('Error occurred:', error);
    }
  };

  const navigateBack = () => {
    navigate('/superadmin/clinic/list');
  };

  return (
    <Card className="col-12">
      <Card.Header>
        <div className="d-sm-flex justify-content-between">
          <h2>Create a new clinic</h2>
        </div>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col xl={12}>
            <Formik
              enableReinitialize={true}
              validationSchema={schema}
              onSubmit={values => onSubmit(values)}
              initialValues={{
                name: '',
                email: '',
                password: '',
                code: '',
                address: '',
                phone: '',
                postcode: '',
                headDietitianName: '',
                headDietitianEmail: '',
                clinical_export_enabled: false,
                research_export_enabled: false
              }}
            >
              {({
                handleSubmit,
                handleChange,
                values,
                touched,
                errors,
                dirty,
                setFieldValue
              }) => {
                const generatePassword = (length = 12) => {
                  const charset =
                    'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=';
                  let password = '';
                  for (let i = 0; i < length; i++) {
                    const randomIndex = Math.floor(
                      Math.random() * charset.length
                    );
                    password += charset[randomIndex];
                  }
                  setFieldValue('password', password);
                };

                const generateCode = async (length = 6) => {
                  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
                  let code = '';
                  for (let i = 0; i < length; i++) {
                    const randomIndex = Math.floor(
                      Math.random() * charset.length
                    );
                    code += charset[randomIndex];
                  }

                  setQrCodeImage(await QRCode.toDataURL(code));

                  setFieldValue('code', code);
                };
                return (
                  <Form noValidate onSubmit={handleSubmit}>
                    <Row className={classNames('g-3', 'mb-6')}>
                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>
                            Clinic Name{' '}
                            <span className="text-danger ms-1">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={values.name}
                            onChange={handleChange}
                            isValid={touched.name && !errors.name}
                            isInvalid={touched.name && !!errors.name}
                          />
                          <Form.Control.Feedback type="invalid">
                            {' '}
                            {errors.name}{' '}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>
                            Clinic Email Address{' '}
                            <span className="text-danger ms-1">*</span>
                          </Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            isValid={touched.email && !errors.email}
                            isInvalid={touched.email && !!errors.email}
                          />
                          <Form.Control.Feedback type="invalid">
                            {' '}
                            {errors.email}{' '}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Clinic Contact</Form.Label>
                          <Form.Control
                            type="text"
                            name="phone"
                            value={values.phone}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Postcode</Form.Label>
                          <Form.Control
                            type="text"
                            name="postcode"
                            value={values.postcode}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>
                            Head Dietitian Name{' '}
                            <span className="text-danger ms-1">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="headDietitianName"
                            value={values.headDietitianName}
                            onChange={handleChange}
                            isValid={
                              touched.headDietitianName &&
                              !errors.headDietitianName
                            }
                            isInvalid={
                              touched.headDietitianName &&
                              !!errors.headDietitianName
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {' '}
                            {errors.headDietitianName}{' '}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>
                            Head Dietitian Email{' '}
                            <span className="text-danger ms-1">*</span>
                          </Form.Label>
                          <Form.Control
                            type="email"
                            name="headDietitianEmail"
                            value={values.headDietitianEmail}
                            onChange={handleChange}
                            isValid={
                              touched.headDietitianEmail &&
                              !errors.headDietitianEmail
                            }
                            isInvalid={
                              touched.headDietitianEmail &&
                              !!errors.headDietitianEmail
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {' '}
                            {errors.headDietitianEmail}{' '}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Check
                            type="switch"
                            name="clinical_export_enabled"
                            id="clinical_export_enabled"
                            label="Allow clinical data export? (With real patient names)"
                            checked={values.clinical_export_enabled}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Check
                            type="switch"
                            name="research_export_enabled"
                            id="research_export_enabled"
                            label="Allow research data export? (Without real patient names)"
                            checked={values.research_export_enabled}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col sm={8} md={8}>
                        <Row className={classNames('g-3', 'mb-6')}>
                          <Col sm={6} md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label>
                                Password{' '}
                                <span className="text-danger ms-1">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="password"
                                value={values.password}
                                onChange={handleChange}
                                isValid={touched.password && !errors.password}
                                isInvalid={
                                  touched.password && !!errors.password
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {' '}
                                {errors.password}{' '}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col sm={3} md={3}>
                            {' '}
                            <br />
                            <Button
                              variant="outline-primary"
                              className="me-2 px-6"
                              onClick={() => generatePassword(12)}
                            >
                              Generate
                            </Button>{' '}
                          </Col>
                          <Col sm={6} md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label>
                                Code <span className="text-danger ms-1">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="code"
                                placeholder="Code"
                                value={values.code}
                                onChange={handleChange}
                                isValid={touched.code && !errors.code}
                                isInvalid={touched.code && !!errors.code}
                              />
                              <Form.Control.Feedback type="invalid">
                                {' '}
                                {errors.code}{' '}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col sm={3} md={3}>
                            {' '}
                            <br />
                            <Button
                              variant="outline-primary"
                              className="me-2 px-6"
                              onClick={() => generateCode(6)}
                            >
                              Generate
                            </Button>{' '}
                          </Col>
                        </Row>
                      </Col>

                      <Col sm={3} md={3}>
                        <a href={qrCodeImage} download={values.code}>
                          <img src={qrCodeImage} alt="" className="w-50" />
                        </a>
                      </Col>

                      <Col sm={12} md={12}>
                        <Form.Group className="mb-2">
                          <Form.Label>Address</Form.Label>
                          <Form.Control
                            as="textarea"
                            name="address"
                            value={values.address}
                            onChange={handleChange}
                            rows={4}
                          />
                        </Form.Group>
                      </Col>

                      <div className="text-end">
                        <Button
                          variant="phoenix-primary"
                          className="me-2 px-6"
                          onClick={navigateBack}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" variant="primary">
                          Create
                        </Button>
                      </div>
                    </Row>
                  </Form>
                );
              }}
            </Formik>
            {createdClinicUser && (
              <Card className="mt-4">
                <Card.Header>
                  <h4 className="mb-0">Created Clinic Admin User</h4>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table bordered hover className="mb-0">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email Address</th>
                          <th>Password</th>
                          <th>Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <span className="fw-bold">
                              {createdClinicUser.firstname}
                            </span>
                          </td>
                          <td>{createdClinicUser.email}</td>
                          <td>
                            <code className="bg-light px-2 py-1">
                              {createdClinicUser.password}
                            </code>
                          </td>
                          <td>
                            <Badge bg="success">{createdClinicUser.role}</Badge>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default SAAddClinic;
